/**
 * useUnifiedExitDetection Hook
 *
 * Unified exit detection system that:
 * - Checks ALL exit devices (not just one)
 * - Makes TARGETED requests per visitor (searches by person ID hash)
 * - Searches for events AFTER visitor entry time only
 * - Tries multiple hash algorithms (MD5, SHA-256, SHA-384) per device
 * - Implements smart caching (30-second TTL)
 * - Polls every 60 seconds
 * - Supports manual refresh with cache bypass
 *
 * Approach:
 * - For each inside visitor → For each exit device → Search for events WHERE:
 *   - employeeNoString = hash(visitor.personId)
 *   - major = 5 (access control)
 *   - minor = 75 (successful authentication)
 *   - time >= visitor.entryTime (only events AFTER entry)
 * - If any events found → visitor has exited
 *
 * Performance:
 * - Requests: ~44/minute (11 visitors × 4 devices × 1 poll/min)
 * - Much less data per request (only events for specific person)
 * - Server-side filtering by person ID and time
 * - Still 93% fewer requests than original (660/min → 44/min)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import md5 from 'md5';
import { ExitDeviceInfo } from './useMultiDeviceExitDetection';
import { VisitorForExitCheck } from './useVisitorExitDetection';
import { logger } from '../services/logger';
import { debugLogStore } from '../components/OperationalView/components/ExitDetectionDebugPanel';
import { isElectron } from '../utils/platform';
import {
  decryptPersonHashFromEvent,
  isLikelyEncrypted,
  decryptHikvisionDataWithMethod,
} from '../utils/hikvisionDecryption';

/**
 * Generate person codes for multiple hash algorithms
 * Different Hikvision devices/firmware use different hash algorithms:
 * - Older models: MD5 (32 chars)
 * - Newer models: SHA-256 (64 chars) or SHA-384 (96 chars)
 *
 * CRITICAL: Backend updates personId to "hik-{hash}" after device sync.
 * If personId starts with "hik-", extract the hash directly instead of hashing again!
 */
const generatePersonCodes = async (personId: string): Promise<string[]> => {
  const codes: string[] = [];

  // CRITICAL FIX: Check if personId is already in "hik-{hash}" format
  // Backend code (device_people_handler.go:637) does: person.PersonID = "hik-" + employeeNo
  // So if we receive "hik-abc123...", the hash is ALREADY computed - just extract it!
  if (personId.startsWith('hik-')) {
    const extractedHash = personId.substring(4); // Remove "hik-" prefix
    logger.debug('unified-exit-detection', `Extracted hash from hik- format: ${extractedHash}`);

    // The extracted hash is what's registered on the device
    codes.push(extractedHash);

    // We don't need to generate other hash types because the backend only uses MD5
    // and the device already has this exact hash registered
    return codes;
  }

  // If personId is NOT in "hik-" format (original UUID), hash it
  // MD5 (32 chars) - this is what the backend uses
  codes.push(md5(personId));

  // SHA-256 (64 chars) and SHA-384 (96 chars) - newer devices (if needed)
  const encoder = new TextEncoder();
  const data = encoder.encode(personId);

  try {
    // SHA-256
    const sha256Buffer = await crypto.subtle.digest('SHA-256', data);
    const sha256Array = Array.from(new Uint8Array(sha256Buffer));
    const sha256Hex = sha256Array.map(b => b.toString(16).padStart(2, '0')).join('');
    codes.push(sha256Hex);

    // SHA-384
    const sha384Buffer = await crypto.subtle.digest('SHA-384', data);
    const sha384Array = Array.from(new Uint8Array(sha384Buffer));
    const sha384Hex = sha384Array.map(b => b.toString(16).padStart(2, '0')).join('');
    codes.push(sha384Hex);
  } catch (error) {
    logger.warn('unified-exit-detection', 'Failed to generate SHA hashes', error);
  }

  return codes;
};

/**
 * Synchronous MD5-only version for backwards compatibility
 */
const generatePersonCodeMD5 = (personId: string): string => {
  return md5(personId.toLowerCase());
};

/**
 * Cache entry structure
 */
interface CacheEntry {
  events: any[]; // Events with decrypted hashes (if applicable)
  timestamp: number;
  expiresAt: number;
  deviceId: string | number;
  iv: string; // Initialization vector used for encryption
  password: string; // Device password (for decryption)
  decryptionMethod?: 'md5' | 'sha256' | 'direct' | null; // Successful decryption method for this device
  hasEncryptedHashes?: boolean; // Whether this device returns encrypted hashes
}

/**
 * Device check status
 */
export interface DeviceCheckStatus {
  deviceId: string | number;
  deviceName: string;
  lastCheckTime: Date;
  totalEvents: number; // Total events returned from device
  eventsFound: number; // Successful events (major=5, minor=75)
  exitsDetected: number;
  cacheHit: boolean;
  error: string | null;
}

/**
 * Hook options
 */
export interface UseUnifiedExitDetectionOptions {
  devices: ExitDeviceInfo[];
  insideVisitors: VisitorForExitCheck[];
  onVisitorExited: (personId: string, roleId: string, deviceId: string | number) => void;
  enabled: boolean;
  pollingInterval?: number; // Default: 30000ms (30 seconds) - OPTIMIZED for faster detection
  cacheTTL?: number; // Default: 30000ms (30 seconds)
  initialScanDays?: number; // Default: 7 days - how far back to scan on first run
}

/**
 * Hook return value
 */
export interface UseUnifiedExitDetectionResult {
  isChecking: boolean;
  lastCheckTime: Date | null;
  deviceStatuses: Map<string | number, DeviceCheckStatus>;
  totalExitsDetected: number;
  performFullScan: () => Promise<void>;
  clearCache: () => void;
  resetTimestamps: () => void; // Reset stored timestamps (forces full historical scan)
  cacheStats: {
    size: number;
    hits: number;
    misses: number;
    hitRate: number;
  };
}

/**
 * Unified exit detection hook
 */
export const useUnifiedExitDetection = (
  options: UseUnifiedExitDetectionOptions
): UseUnifiedExitDetectionResult => {
  const {
    devices,
    insideVisitors,
    onVisitorExited,
    enabled,
    pollingInterval = 30000, // OPTIMIZED: 30 seconds (faster detection!)
    cacheTTL = 15000, // REDUCED: 15 seconds (fresher data!)
    initialScanDays = 1, // CHANGED: 1 day (24 hours) instead of 7 - devices have too many events
  } = options;

  // State
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const [deviceStatuses, setDeviceStatuses] = useState<Map<string | number, DeviceCheckStatus>>(
    new Map()
  );
  const [totalExitsDetected, setTotalExitsDetected] = useState(0);

  // Cache and stats
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const cacheHitsRef = useRef(0);
  const cacheMissesRef = useRef(0);

  // Lock to prevent concurrent polling
  const isPollingRef = useRef(false);

  // Last checked timestamps per device (persisted in localStorage)
  const STORAGE_KEY = 'unified-exit-detection-last-checked';

  // Load last checked timestamps from localStorage
  const loadLastCheckedTimestamps = useCallback((): Map<string | number, Date> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return new Map();

      const parsed = JSON.parse(stored);
      const map = new Map<string | number, Date>();

      Object.entries(parsed).forEach(([deviceId, timestamp]) => {
        map.set(deviceId, new Date(timestamp as string));
      });

      return map;
    } catch (error) {
      logger.error('unified-exit-detection', 'Failed to load last checked timestamps', error);
      return new Map();
    }
  }, []);

  // Save last checked timestamps to localStorage
  const saveLastCheckedTimestamps = useCallback(
    (timestamps: Map<string | number, Date>) => {
      try {
        const obj: Record<string, string> = {};
        timestamps.forEach((timestamp, deviceId) => {
          obj[String(deviceId)] = timestamp.toISOString();
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      } catch (error) {
        logger.error('unified-exit-detection', 'Failed to save last checked timestamps', error);
      }
    },
    []
  );

  // Get start time for device check (uses stored timestamp or falls back to initialScanDays)
  const getStartTimeForDevice = useCallback(
    (deviceId: string | number): Date => {
      const lastChecked = loadLastCheckedTimestamps().get(deviceId);

      if (lastChecked) {
        // CRITICAL FIX: Always check back at least 5 minutes to avoid missing events
        // This handles cases where events happened between polling intervals
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const startTime = lastChecked < fiveMinutesAgo ? lastChecked : fiveMinutesAgo;

        logger.debug('unified-exit-detection', 'Using stored last checked timestamp (with 5min overlap)', {
          deviceId,
          lastChecked: lastChecked.toISOString(),
          actualStartTime: startTime.toISOString(),
          overlapApplied: lastChecked >= fiveMinutesAgo,
        });
        return startTime;
      }

      // First run - scan back 12 hours (NOT full day - too many events)
      // Devices generate 1000+ events per day, so 24 hours hits the limit
      const startTime = new Date(Date.now() - 12 * 60 * 60 * 1000); // 12 hours
      logger.info('unified-exit-detection', 'First run - scanning historical data', {
        deviceId,
        scanningBackHours: 12,
        startTime: startTime.toISOString(),
      });

      return startTime;
    },
    [initialScanDays, loadLastCheckedTimestamps]
  );

  // Update last checked timestamp for device
  const updateLastCheckedTimestamp = useCallback(
    (deviceId: string | number, timestamp: Date) => {
      const timestamps = loadLastCheckedTimestamps();
      timestamps.set(deviceId, timestamp);
      saveLastCheckedTimestamps(timestamps);

      logger.debug('unified-exit-detection', 'Updated last checked timestamp', {
        deviceId,
        timestamp: timestamp.toISOString(),
      });
    },
    [loadLastCheckedTimestamps, saveLastCheckedTimestamps]
  );

  // Cleanup expired cache entries
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    const cache = cacheRef.current;
    const keysToDelete: string[] = [];

    cache.forEach((entry, key) => {
      if (entry.expiresAt <= now) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => cache.delete(key));

    if (keysToDelete.length > 0) {
      logger.debug('unified-exit-detection', 'Cleaned up expired cache entries', {
        removed: keysToDelete.length,
        remaining: cache.size,
      });
    }
  }, []);

  // Clear entire cache
  const clearCache = useCallback(() => {
    const oldSize = cacheRef.current.size;
    cacheRef.current.clear();
    cacheHitsRef.current = 0;
    cacheMissesRef.current = 0;
    logger.info('unified-exit-detection', 'Cache cleared', { oldSize });

    // User feedback in debug panel
    const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
    if (isDebugMode) {
      debugLogStore.addLog(
        'warn',
        ` Cache cleared! Removed ${oldSize} cached entries. Stats reset.`
      );
    }
  }, []);

  // Reset stored timestamps (forces full historical scan on next check)
  const resetTimestamps = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      logger.info('unified-exit-detection', 'Timestamps reset - next check will scan full history', {
        scanDays: initialScanDays,
      });

      // User feedback in debug panel
      const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
      if (isDebugMode) {
        debugLogStore.addLog(
          'warn',
          ' Timestamps reset! Next check will scan last 12 hours of history.'
        );
      }
    } catch (error) {
      logger.error('unified-exit-detection', 'Failed to reset timestamps', error);
    }
  }, [initialScanDays]);

  // =============================================================================
  // CORRECT IMPLEMENTATION: Per-device requests (not per-visitor)
  // - ONE request per device for time window
  // - Check ALL visitors against device's events
  // - 99% fewer requests than per-visitor approach
  // =============================================================================

  // Fetch events from device for a time window
  const fetchDeviceEvents = useCallback(
    async (
      device: ExitDeviceInfo,
      startTime: Date,
      endTime: Date,
      bypassCache: boolean = false
    ): Promise<{ events: any[]; cacheHit: boolean; iv: string; password: string }> => {
      const cacheKey = `${device.id}-${startTime.getTime()}-${endTime.getTime()}`;
      const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';

      // DEBUG: Log cache key
      if (isDebugMode) {
        logger.debug('unified-exit-detection', 'Cache key generated', {
          deviceId: device.id,
          deviceName: device.name,
          cacheKey,
          bypassCache,
        });
      }

      // Check cache first
      if (!bypassCache) {
        const cached = cacheRef.current.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
          cacheHitsRef.current++;
          if (isDebugMode) {
            logger.debug('unified-exit-detection', 'Cache HIT', {
              deviceName: device.name,
              cacheKey,
            });
          }
          return {
            events: cached.events,
            cacheHit: true,
            iv: cached.iv,
            password: cached.password,
          };
        }
      }

      // Cache miss
      cacheMissesRef.current++;
      if (isDebugMode) {
        logger.debug('unified-exit-detection', 'Cache MISS - fetching from device', {
          deviceName: device.name,
          cacheKey,
        });
      }

      // Format timestamps
      const formatTimeWithTZ = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const tzOffset = -date.getTimezoneOffset();
        const tzSign = tzOffset >= 0 ? '+' : '-';
        const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
        const tzMinutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzSign}${tzHours}:${tzMinutes}`;
      };

      // CRITICAL FIX: Remove encryption for AcsEvent endpoint
      // The backend successfully retrieves users WITH encryption, but AcsEvent might not support it
      // Try WITHOUT security parameter first - device should return plain MD5 hashes
      const endpoint = `/ISAPI/AccessControl/AcsEvent?format=json`;
      const url = `http://${device.ip}:${device.port}${endpoint}`;

      // Keep IV for cache key (even though not used in request)
      const generateIV = () => {
        const chars = '0123456789abcdef';
        let iv = '';
        for (let i = 0; i < 32; i++) {
          iv += chars[Math.floor(Math.random() * chars.length)];
        }
        return iv;
      };
      const iv = generateIV();

      // CRITICAL: Request ONLY access control events (major=5) which have person data!
      // major=5, minor=75 = Access control events with person identification
      // DO NOT use major=0 or you'll get operational events without person data!
      const searchCondition: any = {
        searchID: `search_${Date.now()}`,
        searchResultPosition: 0,
        maxResults: 1000,
        major: 5,  // 5 = Access control events (has person data!)
        minor: 75, // 75 = Access granted/denied
        startTime: formatTimeWithTZ(startTime),
        endTime: formatTimeWithTZ(endTime),
      };

      const searchBody = {
        AcsEventCond: searchCondition,
      };

      const response = await window.electronAPI!.deviceHttpRequest({
        url,
        method: 'POST',
        body: searchBody,
        digestAuth: {
          username: device.username,
          password: device.password,
        },
        timeout: 15000,
      });

      if (!response.success) {
        throw new Error(response.error || 'Request failed');
      }

      const responseData = response.data;
      let events: any[] = [];

      if (responseData?.AcsEvent?.InfoList) {
        events = Array.isArray(responseData.AcsEvent.InfoList)
          ? responseData.AcsEvent.InfoList
          : [responseData.AcsEvent.InfoList];
      }

      // Cache the response
      cacheRef.current.set(cacheKey, {
        events,
        timestamp: Date.now(),
        expiresAt: Date.now() + cacheTTL,
        deviceId: device.id,
        iv,
        password: device.password,
      });

      return { events, cacheHit: false, iv, password: device.password };
    },
    [cacheTTL]
  );

  // Check single device for exits (ONE request, check ALL visitors)
  const checkDeviceForExits = useCallback(
    async (
      device: ExitDeviceInfo,
      startTime: Date,
      endTime: Date,
      bypassCache: boolean = false
    ): Promise<DeviceCheckStatus> => {
      try {
        const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';

        // Log the time window being checked
        if (isDebugMode) {
          const timeWindowMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
          debugLogStore.addLog(
            'info',
            ` Checking ${device.name}:\n  From: ${startTime.toLocaleString('pt-BR')}\n  To: ${endTime.toLocaleString('pt-BR')}\n  Window: ${timeWindowMinutes} minutes\n  Event filter: major=5/minor=75 (ACCESS CONTROL ONLY!)\n   Encryption: DISABLED`
          );
        }

        // Fetch ALL events from device (can't filter by person in API)
        const { events: allEvents, cacheHit, iv, password } = await fetchDeviceEvents(
          device,
          startTime,
          endTime,
          bypassCache
        );

        // DEBUG: Show sample event only when manually triggered (bypassCache)
        if (isDebugMode && bypassCache && allEvents.length > 0) {
          const sampleEvent = allEvents[0];
          debugLogStore.addLog(
            'debug',
            ` Sample event from ${device.name}:\n${JSON.stringify(sampleEvent, null, 2)}`
          );
        }

        // Filter for events with person hashes (employeeNoString)
        // AND decrypt encrypted hashes if necessary
        // Single-pass processing with decryption optimization
        const personEvents: any[] = [];
        let detectedDecryptionMethod: 'md5' | 'sha256' | 'direct' | null = null;
        let encryptedCount = 0;
        let decryptedCount = 0;
        let failedCount = 0;
        let plainCount = 0;

        // Get cached decryption method if available
        const cachedEntry = cacheRef.current.get(`${device.id}-${startTime.getTime()}-${endTime.getTime()}`);
        const knownMethod = cachedEntry?.decryptionMethod;

        for (const event of allEvents) {
          // Must have person identifier
          if (!event.employeeNoString) continue;

          // Must be valid hash (not just "361" or other junk)
          const hash = event.employeeNoString;
          if (typeof hash !== 'string' || hash.length < 10) continue;

          // If hash is encrypted (96 chars), decrypt it
          if (isLikelyEncrypted(hash)) {
            encryptedCount++;

            // Use optimized decryption with known method
            const result = decryptHikvisionDataWithMethod(hash, iv, password, knownMethod || undefined);

            if (result) {
              // Remember the successful method for next events
              if (!detectedDecryptionMethod) {
                detectedDecryptionMethod = result.method;
              }

              decryptedCount++;
              personEvents.push({
                ...event,
                employeeNoString: result.decrypted,
                _originalHash: hash,
                _wasEncrypted: true,
                _decryptionMethod: result.method,
              });
            } else {
              // Decryption failed - skip this event (can't match it anyway)
              failedCount++;
            }
          } else {
            // Not encrypted (32 chars) - use as-is
            plainCount++;
            personEvents.push({
              ...event,
              _wasEncrypted: false,
            });
          }
        }

        // Update cache with detected decryption method for future use
        if (detectedDecryptionMethod && cachedEntry) {
          cachedEntry.decryptionMethod = detectedDecryptionMethod;
          cachedEntry.hasEncryptedHashes = true;
        }

        if (isDebugMode) {
          debugLogStore.addLog(
            'info',
            ` ${device.name}: ${allEvents.length} total events, ${personEvents.length} with person data${cacheHit ? ' [CACHED]' : ''}`
          );

          if (personEvents.length === 0 && allEvents.length > 0) {
            debugLogStore.addLog(
              'error',
              ` NO EVENTS WITH PERSON DATA! All ${allEvents.length} events are missing employeeNoString field!\n  This means the API isn't returning person hashes.\n  Check sample event above to see what fields are available.`
            );
          }

          // DEBUG: Show ALL event hashes for comparison
          if (personEvents.length > 0 || encryptedCount > 0) {
            const statusMessage = [
              ` Hash Status:`,
              `   Total: ${personEvents.length} events with person data`,
              `   Plain MD5: ${plainCount}`,
              `   Encrypted (decrypted): ${decryptedCount}`,
              failedCount > 0 ? `   Decryption failed:  ${failedCount} (filtered out)` : null,
              detectedDecryptionMethod ? `   Decryption method: ${detectedDecryptionMethod.toUpperCase()}` : null,
              knownMethod ? `   Used cached method: ${knownMethod.toUpperCase()}` : null,
            ].filter(Boolean).join('\n');

            debugLogStore.addLog(
              encryptedCount > 0 ? 'warn' : 'debug',
              statusMessage
            );

            if (personEvents.length > 0) {
              const eventHashes = personEvents.map((e: any) => {
                const hash = e.employeeNoString;
                const len = hash?.length || 0;
                const status = e._wasEncrypted
                  ? ` [decrypted via ${e._decryptionMethod || 'unknown'}]`
                  : '';
                return `${e.name || 'Unknown'}: ${hash} (${len} chars)${status}`;
              });
              debugLogStore.addLog(
                'debug',
                ` Event hashes (${personEvents.length}):\n${eventHashes.join('\n')}`
              );
            }
          }

          // DEBUG: Show visitors being checked (summary only)
          if (insideVisitors.length > 0) {
            // Show sample visitor hash for comparison
            const sampleVisitor = insideVisitors[0];
            const sampleHash = await generatePersonCodes(sampleVisitor.personId);
            const visitorHashLength = sampleHash[0]?.length || 0;

            debugLogStore.addLog(
              'info',
              ` Checking ${insideVisitors.length} visitor(s) against ${personEvents.length} events...\n` +
              `   Sample visitor hash: ${sampleHash[0]} (${visitorHashLength} chars)\n` +
              `   Visitor: ${sampleVisitor.name}`
            );
          }
        }

        const exitsDetected: string[] = [];

        // Check each visitor against all person events
        for (const visitor of insideVisitors) {
          const visitorHashes = await generatePersonCodes(visitor.personId);
          const entryTime = new Date(visitor.entryTime).getTime();

          // DEBUG: Log visitor hash
          if (isDebugMode) {
            debugLogStore.addLog(
              'debug',
              ` ${visitor.name}:\n  Hash: ${visitorHashes[0]}\n  Entry: ${visitor.entryTime}`
            );
          }

          // Check if any event matches this visitor's hash and is after entry
          const hasExit = personEvents.some((event: any) => {
            const eventHash = event.employeeNoString;
            const eventTime = new Date(event.time).getTime();

            // Check if event hash matches any visitor hash
            const hashMatch = visitorHashes.includes(eventHash);
            const timeMatch = eventTime > entryTime;

            // Only count exit-side reader events (cardReaderKind=2 or cardReaderNo=2).
            // cardReaderKind: 1=entry, 2=exit, 3=passagem.
            // Raw Hikvision device events carry no zone information at all — a device's
            // zone_direction is just its default/primary zone assignment, and a single
            // device can serve multiple readers (entry/exit/passagem) mapped to different
            // zones. Without an explicit exit-side reader confirmation, we cannot know
            // this specific read came from the actual exit zone, so we must NOT assume
            // it did — the zone-aware backend (SSE + access-log scan) already handles
            // this event correctly and is the authoritative source when this is ambiguous.
            const cardReaderKind = event.cardReaderKind ?? event.CardReaderKind;
            const cardReaderNo   = event.cardReaderNo   ?? event.CardReaderNo;
            const isExitSide = cardReaderKind === 2 || cardReaderNo === 2;

            if (isDebugMode && hashMatch && timeMatch) {
              debugLogStore.addLog(
                isExitSide ? 'warn' : 'info',
                ` ${visitor.name}: ${isExitSide ? 'EXIT DETECTED' : 'SKIPPED (entry/passagem side)'}\n  Event: ${eventHash}\n  cardReaderKind: ${cardReaderKind} cardReaderNo: ${cardReaderNo}\n  Event Time: ${event.time}\n  Entry Time: ${visitor.entryTime}`
              );
            }

            return hashMatch && timeMatch && isExitSide;
          });

          if (hasExit) {
            exitsDetected.push(visitor.personId);
            onVisitorExited(visitor.personId, visitor.roleId, device.id);

            if (isDebugMode) {
              debugLogStore.addLog(
                'warn',
                ` EXIT DETECTED: ${visitor.name} on ${device.name}`
              );
            }
          }
        }

        return {
          deviceId: device.id,
          deviceName: device.name,
          lastCheckTime: new Date(),
          totalEvents: allEvents.length,
          eventsFound: personEvents.length,
          exitsDetected: exitsDetected.length,
          cacheHit,
          error: null,
        };
      } catch (error: any) {
        logger.error('unified-exit-detection', 'Device check failed', {
          deviceId: device.id,
          deviceName: device.name,
          error,
        });

        return {
          deviceId: device.id,
          deviceName: device.name,
          lastCheckTime: new Date(),
          totalEvents: 0,
          eventsFound: 0,
          exitsDetected: 0,
          cacheHit: false,
          error: error?.message || 'Unknown error',
        };
      }
    },
    [fetchDeviceEvents, insideVisitors, onVisitorExited, cacheTTL]
  );


  // Check all devices
  const checkAllDevices = useCallback(
    async (bypassCache: boolean = false) => {
      // CRITICAL: Check if feature is enabled
      if (!enabled) {
        logger.debug('unified-exit-detection', 'Device check skipped - feature disabled');
        return;
      }

      // Check if already polling - prevent concurrent cycles
      if (isPollingRef.current) {
        logger.debug('unified-exit-detection', 'Polling already in progress, skipping this cycle');
        return;
      }

      if (devices.length === 0) {
        logger.debug('unified-exit-detection', 'No devices to check');
        return;
      }

      if (insideVisitors.length === 0) {
        logger.debug('unified-exit-detection', 'No inside visitors to check');

        const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
        if (isDebugMode) {
          debugLogStore.addLog(
            'info',
            ' No inside visitors to check - waiting...'
          );
        }
        return;
      }

      // Set lock
      isPollingRef.current = true;
      setIsChecking(true);

      try {
        const now = new Date();

        logger.info('unified-exit-detection', 'Starting device checks', {
          deviceCount: devices.length,
          visitorCount: insideVisitors.length,
          bypassCache,
        });

        // Find the earliest visitor entry time - that's when we need to start searching!
        const earliestEntryTime = Math.min(
          ...insideVisitors.map(v => new Date(v.entryTime).getTime())
        );

        // CRITICAL: Search from earliest entry time, NOT from arbitrary window!
        // Add 1 minute buffer before entry to catch any timing issues
        const searchStartTime = new Date(earliestEntryTime - 1 * 60 * 1000); // 1 min before earliest entry

        // Check devices SEQUENTIALLY (not parallel) to avoid overwhelming device
        const results: PromiseSettledResult<DeviceCheckStatus>[] = [];

        for (const device of devices) {
          try {
            // Use earliest entry time as start (not fixed window!)
            const startTime = searchStartTime;

            logger.debug('unified-exit-detection', 'Checking device', {
              deviceId: device.id,
              deviceName: device.name,
              startTime: startTime.toISOString(),
              endTime: now.toISOString(),
              timeWindowMinutes: Math.round((now.getTime() - startTime.getTime()) / 60000),
            });

            // IMPROVED: Always bypass cache for checks (ensures freshest data)
            const result = await checkDeviceForExits(device, startTime, now, true);
            results.push({ status: 'fulfilled', value: result });
          } catch (error) {
            results.push({ status: 'rejected', reason: error });
          }

          // OPTIMIZED: Reduced delay from 2000ms to 500ms for faster detection
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Process results
        const newStatuses = new Map<string | number, DeviceCheckStatus>();
        let totalExits = 0;

        results.forEach((result, index) => {
          const device = devices[index];

          if (result.status === 'fulfilled') {
            const status = result.value;
            newStatuses.set(device.id, status);
            totalExits += status.exitsDetected;
          } else {
            // Failed check
            newStatuses.set(device.id, {
              deviceId: device.id,
              deviceName: device.name,
              lastCheckTime: new Date(),
              totalEvents: 0,
              eventsFound: 0,
              exitsDetected: 0,
              cacheHit: false,
              error: result.reason?.message || 'Unknown error',
            });
          }
        });

        setDeviceStatuses(newStatuses);
        setTotalExitsDetected((prev) => prev + totalExits);
        setLastCheckTime(new Date());

        logger.info('unified-exit-detection', 'Device checks completed', {
          deviceCount: devices.length,
          totalExitsDetected: totalExits,
          successfulChecks: results.filter((r) => r.status === 'fulfilled').length,
          failedChecks: results.filter((r) => r.status === 'rejected').length,
        });
      } catch (error) {
        logger.error('unified-exit-detection', 'Check all devices failed', error);
      } finally {
        // Release lock
        isPollingRef.current = false;
        setIsChecking(false);
        cleanupCache();
      }
    },
    [enabled, devices, insideVisitors, checkDeviceForExits, cleanupCache, getStartTimeForDevice, updateLastCheckedTimestamp]
  );

  // Manual full scan (multiple 5-minute windows to work around 30-event limit)
  const performFullScan = useCallback(async () => {
    // CRITICAL: Check if feature is enabled
    if (!enabled) {
      logger.debug('unified-exit-detection', 'Full scan skipped - feature disabled');
      return;
    }

    // Check if already polling - prevent overlap with automatic polling
    if (isPollingRef.current) {
      logger.warn('unified-exit-detection', 'Polling in progress, waiting before full scan');
      // Wait a bit and try again (this is a user-triggered action)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // If still busy, abort
      if (isPollingRef.current) {
        logger.warn('unified-exit-detection', 'Full scan aborted - polling still in progress');
        return;
      }
    }

    if (devices.length === 0) {
      logger.debug('unified-exit-detection', 'Full scan skipped - no devices');
      return;
    }

    if (insideVisitors.length === 0) {
      logger.debug('unified-exit-detection', 'Full scan skipped - no inside visitors');
      return;
    }

    // Set lock
    isPollingRef.current = true;
    setIsChecking(true);

    try {
      const now = new Date();

      // Find the earliest visitor entry time - search from there!
      const earliestEntryTime = Math.min(
        ...insideVisitors.map(v => new Date(v.entryTime).getTime())
      );

      // CRITICAL: Search from earliest entry time (with small buffer)
      const startTime = new Date(earliestEntryTime - 1 * 60 * 1000); // 1 min before earliest entry

      const timeWindowMinutes = Math.round((now.getTime() - startTime.getTime()) / 60000);

      logger.info('unified-exit-detection', 'Starting full scan', {
        deviceCount: devices.length,
        visitorCount: insideVisitors.length,
        timeWindow: `${timeWindowMinutes} minutes (from earliest entry)`,
        earliestEntry: new Date(earliestEntryTime).toLocaleString('pt-BR'),
      });

      const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
      if (isDebugMode) {
        debugLogStore.addLog(
          'warn',
          ` Manual scan: Checking from ${new Date(earliestEntryTime).toLocaleString('pt-BR')} (earliest entry) to now`
        );
      }

      // Check devices SEQUENTIALLY (not parallel) - avoid overwhelming device
      const results: PromiseSettledResult<DeviceCheckStatus>[] = [];

      for (const device of devices) {
        try {
          const result = await checkDeviceForExits(device, startTime, now, true);
          results.push({ status: 'fulfilled', value: result });
        } catch (error) {
          results.push({ status: 'rejected', reason: error });
        }

        // OPTIMIZED: Reduced delay for faster manual scans
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Process results
      const newStatuses = new Map<string | number, DeviceCheckStatus>();
      let totalExits = 0;

      results.forEach((result, index) => {
        const device = devices[index];

        if (result.status === 'fulfilled') {
          const status = result.value;
          newStatuses.set(device.id, status);
          totalExits += status.exitsDetected;
        } else {
          newStatuses.set(device.id, {
            deviceId: device.id,
            deviceName: device.name,
            lastCheckTime: new Date(),
            totalEvents: 0,
            eventsFound: 0,
            exitsDetected: 0,
            cacheHit: false,
            error: result.reason?.message || 'Unknown error',
          });
        }
      });

      setDeviceStatuses(newStatuses);
      setTotalExitsDetected((prev) => prev + totalExits);
      setLastCheckTime(new Date());

      logger.info('unified-exit-detection', 'Full scan completed', {
        deviceCount: devices.length,
        totalExitsDetected: totalExits,
        successfulChecks: results.filter((r) => r.status === 'fulfilled').length,
        failedChecks: results.filter((r) => r.status === 'rejected').length,
      });
    } catch (error) {
      logger.error('unified-exit-detection', 'Full scan failed', error);
    } finally {
      // Release lock
      isPollingRef.current = false;
      setIsChecking(false);
      cleanupCache();
    }
  }, [enabled, devices, insideVisitors, checkDeviceForExits, cleanupCache]);

  // Polling effect
  useEffect(() => {
    if (!enabled) {
      logger.debug('unified-exit-detection', 'Polling disabled');
      return;
    }

    if (devices.length === 0) {
      logger.warn('unified-exit-detection', ' NO DEVICES - polling disabled', {
        devicesArray: devices,
        devicesLength: devices.length,
      });

      const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
      if (isDebugMode) {
        debugLogStore.addLog(
          'error',
          ' NO EXIT DEVICES FOUND - Check device configuration!'
        );
      }
      return;
    }

    logger.info('unified-exit-detection', 'Starting polling', {
      interval: pollingInterval,
      deviceCount: devices.length,
      deviceNames: devices.map(d => d.name),
      cacheTTL,
    });

    const isDebugMode = isElectron() && process.env.NODE_ENV === 'development';
    if (isDebugMode) {
      debugLogStore.addLog(
        'info',
        ` Starting exit detection for ${devices.length} device(s): ${devices.map(d => d.name).join(', ')}`
      );
    }

    // Initial check
    checkAllDevices();

    // Set up polling interval
    const intervalId = setInterval(() => {
      checkAllDevices();
    }, pollingInterval);

    return () => {
      clearInterval(intervalId);
      logger.info('unified-exit-detection', 'Polling stopped');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, devices, pollingInterval, cacheTTL]);

  // Cache stats
  const cacheStats = {
    size: cacheRef.current.size,
    hits: cacheHitsRef.current,
    misses: cacheMissesRef.current,
    hitRate:
      cacheHitsRef.current + cacheMissesRef.current > 0
        ? cacheHitsRef.current / (cacheHitsRef.current + cacheMissesRef.current)
        : 0,
  };

  return {
    isChecking,
    lastCheckTime,
    deviceStatuses,
    totalExitsDetected,
    performFullScan,
    clearCache,
    resetTimestamps,
    cacheStats,
  };
};
